import { Download, Locked } from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'
import { ReactNode } from 'react';
import { z } from 'zod';

import { listReportQueryOptions } from '@/api/listReport';
import { userCardQueryOptions } from '@/api/userCard';
import { useAuthStore } from '@/auth/store';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { Avatar } from '@/components/input/avatar';
import { MyReport } from '@/components/report/myReport';
import { CampaignIdSchema } from '@/schemas/campaignId';
import { formatPeriod } from '@/util/campaignDisplay';
import { isNotFoundError } from '@/util/httpError';
import { toReportForms } from '@/util/reportForms';

export const Route = createFileRoute('/dashboard/_authenticated/report/$campaignId')({
  component: RouteComponent,
  params: {
    parse: (parameters) => z.object({ campaignId: z.preprocess(Number, CampaignIdSchema) }).parse(parameters)
  },
  errorComponent: () => <ReportMessage>This report link is invalid.</ReportMessage>,
})

function ReportMessage({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "error" }) {
  return (
    <div className={`
      text-body2
      ${tone === "error" ? "text-error-300" : "text-neutral-600"}
    `}>
      {children}
    </div>
  );
}

/**
 * The signed-in employee's own report for the campaign in the URL. Content comes only from the
 * server's `reports` (their CLOSED evaluations, published campaigns only) — never from
 * `campaign.evaluationList`, which holds every evaluation of the campaign, colleagues' included.
 */
function RouteComponent() {
  const match = Route.useMatch();
  const { campaignId } = Route.useParams();
  const { userId } = useAuthStore();

  const { data, error, isError, isPending, refetch } = useQuery(listReportQueryOptions(userId!, campaignId));
  const { data: userCard } = useQuery(userCardQueryOptions(userId!));

  if (isPending) {
    return <ReportMessage>Loading...</ReportMessage>;
  }

  if (isNotFoundError(error)) {
    return <ReportMessage tone="error">Report not found for this campaign.</ReportMessage>;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-2 text-body2">
        <div className="text-error-300">Couldn't load this report.</div>
        <button
          className="
            cursor-pointer text-primary-default underline
            hover:text-primary-700
          "
          onClick={() => void refetch()}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  const { campaign } = data;
  const forms = toReportForms(data.reports, userId!);
  const hasContent = campaign.isPublished && forms.length > 0;

  return (
    <>
      <Breadcrumbs crumbsToAppend={[{
        label: campaign.name,
        path: match.pathname
      }]} />
      <div className="flex flex-col gap-5">
        <div className="
          flex flex-col gap-4 border-b border-[#DDD3EE] pb-[18px]
          sm:flex-row sm:items-end sm:justify-between
        ">
          <div className="flex items-center gap-4">
            {userCard?.name && (
              <Avatar name={userCard.name} pictureBase64={userCard.pictureBase64} size="lg" />
            )}
            <div className="flex min-w-0 flex-col gap-1">
              <h1 className="text-page-title">{userCard?.name}</h1>
              <span className="text-body3 text-neutral-600">
                {campaign.name} · {formatPeriod(campaign.startsAt, campaign.endsAt)}
              </span>
            </div>
          </div>
          {hasContent && (
            <button
              className="
                flex min-h-11 shrink-0 cursor-pointer items-center gap-2
                self-start rounded-lg border-[1.5px] border-primary-default
                bg-neutral-white px-4 text-body3 font-semibold
                text-primary-default
                hover:bg-primary-50
                focus-visible:outline-2 focus-visible:outline-offset-2
                focus-visible:outline-primary-default
                sm:self-auto
                print:hidden
              "
              onClick={() => globalThis.print()}
              type="button"
            >
              <Download aria-hidden size={18} />
              Download PDF
            </button>
          )}
        </div>
        {!campaign.isPublished && (
          <div className="text-body2 text-neutral-600">This report isn't published yet.</div>
        )}
        {campaign.isPublished && forms.length === 0 && (
          <div className="text-body2 text-neutral-600">
            No report available yet for this campaign.
          </div>
        )}
        {hasContent && (
          <>
            <div className="
              flex items-center gap-2.5 rounded-[10px] border border-info-100
              bg-info-50 px-4 py-3 text-body3 text-info-300
            " role="note">
              <Locked aria-hidden className="shrink-0" size={16} />
              <span>Colleagues’ answers are anonymous: you see what was said, never who said it.</span>
            </div>
            <MyReport fallbackTitle={campaign.name} forms={forms} />
          </>
        )}
      </div>
    </>
  );
}
